import { Link } from "react-router-dom";
import { Footer } from "./Footer";

const gold = "#A08448";
const espresso = "#1A120C";

const Section = ({ kicker, title, children, testId }) => (
  <section className="border-t border-[#C4A36A]/35 pt-12" data-testid={testId}>
    <p className="text-[11px] font-medium uppercase tracking-[0.22em]" style={{ color: gold }}>
      {kicker}
    </p>
    <h2 className="mt-3 font-serif text-3xl font-medium leading-tight tracking-tight md:text-4xl" style={{ color: espresso }}>
      {title}
    </h2>
    <div className="mt-5 max-w-2xl space-y-4 font-serif text-base leading-relaxed text-[#3A2C20] md:text-lg">
      {children}
    </div>
  </section>
);

const AboutPage = () => (
  <div className="min-h-screen bg-[#F7F3EA] font-sans text-[#1A120C]" data-testid="about-page">
    <header className="border-b border-[#C4A36A]/25 bg-[#F7F3EA]">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-6 py-4 md:px-10">
        <Link to="/" className="flex items-center gap-3" data-testid="about-logo">
          <img src="/assets/hero-crest.png?v=2" alt="" className="h-10 w-auto" />
          <span className="font-serif text-sm font-medium uppercase tracking-[0.22em] text-[#A08448]">
            Community Hearts
          </span>
        </Link>
        <Link
          to="/"
          className="text-[11px] font-medium uppercase tracking-[0.18em] text-[#A08448] transition-colors hover:text-[#7A5A14]"
          data-testid="about-back-link"
        >
          Back to the site
        </Link>
      </div>
    </header>

    <main className="mx-auto max-w-5xl px-6 py-16 md:px-10 md:py-24">
      <p className="text-[11px] font-medium uppercase tracking-[0.24em] text-[#A08448]" data-testid="about-overline">
        About us
      </p>
      <h1
        className="mt-5 max-w-3xl font-serif text-4xl font-medium leading-[1.05] tracking-tight md:text-6xl"
        style={{ color: espresso }}
        data-testid="about-headline"
      >
        Built for people who show up.
      </h1>
      <p className="mt-5 font-serif text-xl italic md:text-2xl" style={{ color: gold }} data-testid="about-subhead">
        A quieter standard for meeting someone real.
      </p>
      <span className="mt-8 block h-px w-16 bg-[#C4A36A]" aria-hidden="true" />

      <div className="mt-14 space-y-16">
        <Section kicker="The standard" title="No hookups. No games. Intention only." testId="about-who">
          <p>
            Community Hearts is a private membership for adults who are done collecting matches. Every member answers for
            intention before they meet anyone. If you are here for a hookup, a distraction, or a second life, you are in
            the wrong room.
          </p>
          <p>
            Introductions are arranged with care. Photos are selfie-checked. Conversation is meant to become something —
            a first meeting, a clear no, or an honest maybe that does not linger for months. The standard is simple:
            show up as yourself, or do not come in.
          </p>
        </Section>

        <Section kicker="The first city" title="Richmond first. Then the cities that ask." testId="about-richmond">
          <p>
            We open in Richmond. Not everywhere at once. A first city lets us keep the room small, the service personal,
            and the standard visible.
          </p>
          <p>
            After that, we grow city by city. The waitlist tells us where the interest is deepest. The cities that show
            up first are the cities that open next. There is no nationwide dump of strangers. There is a door, then
            another, when the last one is ready.
          </p>
        </Section>

        <Section kicker="Five days" title="A match is a window, not a collection." testId="about-five-days">
          <p>
            When two people match, they have five days. Voice notes. A real conversation. Then the match closes. Nobody
            can bench you, archive you, or keep you as an option in a stack of names.
          </p>
          <p>
            The window exists because time tells the truth. Five days is long enough to be kind and short enough to be
            honest. If it matters, you will make the time. If it does not, it ends cleanly — without a graveyard of
            maybes.
          </p>
        </Section>

        <Section kicker="The founder" title="Justin Demery built this on purpose." testId="about-founder">
          <div className="flex flex-col gap-8 sm:flex-row sm:items-start">
            <img
              src="/founder.jpg"
              alt="Justin Demery, founder of Community Hearts"
              className="h-40 w-40 shrink-0 rounded-full object-cover ring-1 ring-[#C4A36A]/40"
              style={{ objectPosition: "center 30%" }}
              data-testid="about-founder-portrait"
            />
            <div className="space-y-4">
              <p>
                Justin Demery is the founder and CEO. He built Community Hearts after watching too many brilliant, kind
                people come home from dating apps feeling worse than when they opened them. Dating had become a game of
                attention instead of a search for connection.
              </p>
              <p>
                This is his answer: verified members, a five-day window, and a room that refuses to treat people as a
                feed. If you are done playing games, you were always who he was building this for.
              </p>
            </div>
          </div>
        </Section>

        <Section kicker="The feeling" title="Quiet. Elevated. Never a game." testId="about-feeling">
          <p>
            The app is meant to feel like a private club that happens to live on your phone. Soft, unhurried, discreet.
            No streaks. No public scores. No noisy prompts designed to keep you tapping.
          </p>
          <p>
            Privileges are anticipated. Details are considered. Service stays in the background. You should leave a
            session clearer than you arrived — not more restless. That is the test. If it feels like a game, we have
            failed.
          </p>
        </Section>
      </div>

      <div className="mt-20 border-t border-[#C4A36A]/35 pt-12 text-center">
        <p className="font-serif text-2xl italic" style={{ color: gold }}>
          A place is being held.
        </p>
        <Link
          to="/#waitlist"
          className="mt-8 inline-flex items-center gap-3 rounded-full bg-[#C0965A] px-9 py-3.5 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#FFF8E8] transition-colors hover:bg-[#B0894C]"
          data-testid="about-waitlist-link"
        >
          Request an invitation
          <span aria-hidden="true">›</span>
        </Link>
      </div>
    </main>

    <Footer />
  </div>
);

export default AboutPage;
